import { NodeData } from 'src/app/interfaces/node-models.interface';
import { RuntimeDataEntry, RuntimeDataEntryAction } from 'src/app/interfaces/runtime-data.interface';
import { SystemHealth } from 'src/app/interfaces/system-health.interface';
import { AclUserModel } from 'src/app/interfaces/user.interface';
import { CriteriaKeyInfo, ValueInfo, ViewModel } from 'src/app/interfaces/views.interface';
import { WorkflowCardData } from 'src/app/interfaces/workflow.interface';

const basicCollator = new Intl.Collator();
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/**
 * Generically generates a function that sorts alphabetically by the desired ordered properties.
 * e.g. `triggers.sort(alphabeticallyBy(n => n.appName, n => n.segmentName, n => n.friendlyName));`
 * @param pluckers - ordered functions which specify how to get the properties being compared
 * @returns - the generated sort function
 */
export function alphabeticallyBy<T>(...pluckers: ((o: T) => string)[]): (a: T, b: T) => number {
	return (a: T, b: T) => {
		for (const p of pluckers) {
			const result = collator.compare(p(a), p(b));
			if (result !== 0) return result;
		}
		return 0;
	};
}
export const stringsAlphabetically = (a: string, b: string) => collator.compare(a, b);
export const keysAlphabetically = (a: CriteriaKeyInfo, b: CriteriaKeyInfo) => collator.compare(a.keyName, b.keyName);
export const valuesAlphabetically = (a: ValueInfo, b: ValueInfo) => collator.compare(a.value, b.value);
export const workspacesAlphabetically = (a: ViewModel, b: ViewModel) => collator.compare(a.name, b.name);
export const runtimeEntriesReverseChronologically = (a: RuntimeDataEntry, b: RuntimeDataEntry) =>
	basicCollator.compare(b.scheduleTimestamp, a.scheduleTimestamp);
export const runtimeActionsChronologically = (a: RuntimeDataEntryAction, b: RuntimeDataEntryAction) =>
	basicCollator.compare(a.lastUpdatedAt, b.lastUpdatedAt);
export const workspaceAclUsersAlphabetically = (a: AclUserModel, b: AclUserModel) =>
	collator.compare(`${a.firstName} ${a.lastName}`, `${b.firstName} ${b.lastName}`);
export const workflowsAlphabetically = (a: WorkflowCardData, b: WorkflowCardData) => collator.compare(a.name, b.name);

export const activeIncidentsByOpenTime = (a: SystemHealth, b: SystemHealth) =>
	collator.compare(b.createdAt, a.createdAt);
export const closedIncidentsByClosedTime = (a: SystemHealth, b: SystemHealth) => b._ts - a._ts;

export const workflowsAlphabeticallyFirstByWorkspace = (a: WorkflowCardData, b: WorkflowCardData) => {
	// Display all workflows without workspaces first
	const hasWorkspaceSort = (a.workspaceId ? 1 : 0) - (b.workspaceId ? 1 : 0);
	if (hasWorkspaceSort === 0) {
		// followed by workflows with workspaces grouped alphabetically by workspace name
		const workspaceSort = collator.compare(a.workspaceName, b.workspaceName);
		if (workspaceSort === 0) {
			// If 2+ workspaces have the same name, we need to make sure their workflows are sub-sub-sorted by workspaceId
			// Without this step, the swimlanes are likely to display improperly (given 2+ workspaces have the same name)
			const workspaceIdSort = collator.compare(a.workspaceId, b.workspaceId);
			if (workspaceIdSort === 0) {
				// finally, sub-sub-sub-sort everything by workflow name
				return collator.compare(a.name, b.name);
			}
			return workspaceIdSort;
		} else {
			return workspaceSort;
		}
	} else {
		return hasWorkspaceSort;
	}
};

/**
 * Sort nodes alphabetically first by appName, then by segmentId and then by nodeTitle
 */
export const nodesAlphabetically = (a: NodeData, b: NodeData) => {
	const appNameSort = collator.compare(a.appName, b.appName);
	if (appNameSort === 0) {
		const segmentSort = collator.compare(a.segmentId, b.segmentId);
		if (segmentSort === 0) {
			return collator.compare(a.friendlyName, b.friendlyName);
		}
		return segmentSort;
	}
	return appNameSort;
};

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
